import React, { useState } from "react";
import { getStates } from "../../services/helpers";
import ToolTip from "../component-tools/ToolTip";
import MonthYearPicker from "../component-tools/MonthYearPicker";
import ConfirmPopup from "../client-components/ConfirmPopup";

export interface EducationalInstitution {
  eiInstitutionName: string;
  eiContactPerson: string;
  eiConfidentialName: boolean;
  eiEmail: string;
  eiPhone: string;
  eiAddress: string;
  eiCity: string;
  eiState: string;
  eiZipCode: string;
  eiFromDate: Date;
  eiToDate: Date;
  eiCurrent: boolean;
  eiMailingAddressDifferent: boolean;
  eiMailingAddress: string;
  eiMailingCity: string;
  eiMailingState: string;
  eiMailingZipCode: string;
}

interface EducationalInstitutionFormProps {
  educationalInstitution: EducationalInstitution;
  validationErrors: { [key: string]: string };
  onInputChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
    index: number,
  ) => void;
  onDelete: () => void;
  index: number;
}

const EducationalInstitutionForm: React.FC<EducationalInstitutionFormProps> = ({
  educationalInstitution,
  validationErrors,
  onInputChange,
  onDelete,
  index,
}) => {
  const states = getStates();
  const [
    showConfirmationConfidentialContact,
    setShowConfirmationConfidentialContact,
  ] = useState(false);
  return (
    <div className="data-container">
      <div className="text-sub-sub-header" style={{ flex: "0 0 100%" }}>
        Educational Institution #{index + 1}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 40%" }}>
        <label className="field-label">Name of Educational Institution </label>
        <input
          className={`form-control ${
            validationErrors[`eiInstitutionName-${index}`] ? "is-invalid" : ""
          }`}
          type="text"
          value={educationalInstitution.eiInstitutionName || ""}
          onChange={(e) => onInputChange(e, "eiInstitutionName", index)}
        />
        {validationErrors[`eiInstitutionName-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`eiInstitutionName-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 30%" }}>
        <label className="field-label">Contact Person:</label>
        <input
          className={`form-control ${
            validationErrors[`eiContactPerson-${index}`] ? "is-invalid" : ""
          }`}
          type="text"
          value={educationalInstitution.eiContactPerson || ""}
          onChange={(e) => onInputChange(e, "eiContactPerson", index)}
        />
        {validationErrors[`eiContactPerson-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`eiContactPerson-${index}`]}
          </div>
        )}
      </div>

      <div className="checkbox-long">
        <div className="field-input" style={{ flex: "0 0 3%" }}>
          <input
            className="form-check-input"
            type="checkbox"
            checked={educationalInstitution.eiConfidentialName}
            onChange={(e) => {
              onInputChange(
                e as React.ChangeEvent<HTMLInputElement>,
                "eiConfidentialName",
                index,
              );
              setShowConfirmationConfidentialContact(true);
            }}
          />
        </div>
        <div style={{ display: "flex", gap: "5px" }}>
          <label className="field-label">Confidential Contact</label>
          <ToolTip
            tooltipText="Check here to delay contact with this entity, noting this may slow file processing while we confirm with client."
            placement="top"
            className="info-icon-spec"
          ></ToolTip>
        </div>
        <div></div>
      </div>
      {showConfirmationConfidentialContact &&
        educationalInstitution.eiConfidentialName && (
          <ConfirmPopup
            onConfirm={() => {
              setShowConfirmationConfidentialContact(false);
            }}
            onCancel={() => setShowConfirmationConfidentialContact(false)}
            message="By checking this box, you are requesting that we do not contact this entity. You understand this may delay your background check while we confirm approval from the client to move forward without that contact."
          />
        )}

      <div className="field-stacked" style={{ flex: "0 0 40%" }}>
        <label className="field-label">Email:</label>
        <input
          className={`form-control ${
            validationErrors[`eiEmail-${index}`] ? "is-invalid" : ""
          }`}
          type="email"
          value={educationalInstitution.eiEmail || ""}
          onChange={(e) => onInputChange(e, "eiEmail", index)}
        />
        {validationErrors[`eiEmail-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`eiEmail-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">Phone:</label>
        <input
          className={`form-control ${
            validationErrors[`eiPhone-${index}`] ? "is-invalid" : ""
          }`}
          type="tel"
          value={educationalInstitution.eiPhone || ""}
          onChange={(e) => onInputChange(e, "eiPhone", index)}
        />
        {validationErrors[`eiPhone-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`eiPhone-${index}`]}
          </div>
        )}
      </div>
      <div className="field-stacked" style={{ flex: "0 0 20%" }}></div>

      <div className="field-stacked" style={{ flex: "0 0 30%" }}>
        <label className="field-label">Address:</label>
        <input
          className={`form-control ${
            validationErrors[`eiAddress-${index}`] ? "is-invalid" : ""
          }`}
          type="text"
          value={educationalInstitution.eiAddress || ""}
          onChange={(e) => onInputChange(e, "eiAddress", index)}
        />
        {validationErrors[`eiAddress-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`eiAddress-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">City:</label>
        <input
          className={`form-control ${
            validationErrors[`eiCity-${index}`] ? "is-invalid" : ""
          }`}
          type="text"
          value={educationalInstitution.eiCity || ""}
          onChange={(e) => onInputChange(e, "eiCity", index)}
        />
        {validationErrors[`eiCity-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`eiCity-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">State:</label>
        <select
          className={`form-select ${
            validationErrors[`eiState-${index}`] ? "is-invalid" : ""
          }`}
          value={educationalInstitution.eiState || ""}
          onChange={(e) => onInputChange(e, "eiState", index)}
        >
          <option value="">Select State</option>
          {states.map((state) => (
            <option key={state.abbreviation} value={state.abbreviation}>
              {state.name} ({state.abbreviation})
            </option>
          ))}
        </select>
        {validationErrors[`eiState-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`eiState-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">Zip Code:</label>
        <input
          className={`form-control ${
            validationErrors[`eiZipCode-${index}`] ? "is-invalid" : ""
          }`}
          type="text"
          value={educationalInstitution.eiZipCode || ""}
          onChange={(e) => onInputChange(e, "eiZipCode", index)}
        />
        {validationErrors[`eiZipCode-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`eiZipCode-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">From Date:</label>
        <MonthYearPicker
          value={educationalInstitution.eiFromDate}
          onChange={(newDate) =>
            onInputChange(
              { target: { value: newDate } } as any,
              "eiFromDate",
              index,
            )
          }
        />
        {validationErrors[`eiFromDate-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`eiFromDate-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">To Date:</label>
        <MonthYearPicker
          value={educationalInstitution.eiToDate}
          onChange={(newDate) =>
            onInputChange(
              { target: { value: newDate } } as any,
              "eiToDate",
              index,
            )
          }
        />
        {validationErrors[`eiToDate-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`eiToDate-${index}`]}
          </div>
        )}
      </div>

      <div className="field-long" style={{ flex: "0 0 20%" }}>
        <label className="field-label">Is Current:</label>
        <div>
          <input
            className="form-check-input"
            type="checkbox"
            checked={educationalInstitution.eiCurrent}
            onChange={(e) =>
              onInputChange(
                e as React.ChangeEvent<HTMLInputElement>,
                "eiCurrent",
                index,
              )
            }
          />
        </div>
      </div>

      <div className="field-long" style={{ flex: "0 0 100%" }}>
        <div>
          <input
            className="form-check-input"
            type="checkbox"
            checked={educationalInstitution.eiMailingAddressDifferent}
            onChange={(e) =>
              onInputChange(
                e as React.ChangeEvent<HTMLInputElement>,
                "eiMailingAddressDifferent",
                index,
              )
            }
          />
        </div>
        <label className="field-label" style={{ textWrap: "wrap" }}>
          Mailing Address Different than Physical Address
        </label>
      </div>

      {educationalInstitution.eiMailingAddressDifferent && (
        <>
          <div className="field-stacked" style={{ flex: "0 0 40%" }}>
            <label className="field-label">Mailing Address:</label>
            <input
              className={`form-control ${
                validationErrors[`eiMailingAddress-${index}`]
                  ? "is-invalid"
                  : ""
              }`}
              type="text"
              value={educationalInstitution.eiMailingAddress || ""}
              onChange={(e) => onInputChange(e, "eiMailingAddress", index)}
            />
            {validationErrors[`eiMailingAddress-${index}`] && (
              <div className="invalid-feedback">
                {validationErrors[`eiMailingAddress-${index}`]}
              </div>
            )}
          </div>

          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Mailing City:</label>
            <input
              className={`form-control ${
                validationErrors[`eiMailingCity-${index}`] ? "is-invalid" : ""
              }`}
              type="text"
              value={educationalInstitution.eiMailingCity || ""}
              onChange={(e) => onInputChange(e, "eiMailingCity", index)}
            />
            {validationErrors[`eiMailingCity-${index}`] && (
              <div className="invalid-feedback">
                {validationErrors[`eiMailingCity-${index}`]}
              </div>
            )}
          </div>

          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Mailing State:</label>
            <select
              className={`form-select ${
                validationErrors[`eiMailingState-${index}`] ? "is-invalid" : ""
              }`}
              value={educationalInstitution.eiMailingState || ""}
              onChange={(e) => onInputChange(e, "eiMailingState", index)}
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.abbreviation} value={state.abbreviation}>
                  {state.name} ({state.abbreviation})
                </option>
              ))}
            </select>
            {validationErrors[`eiMailingState-${index}`] && (
              <div className="invalid-feedback">
                {validationErrors[`eiMailingState-${index}`]}
              </div>
            )}
          </div>

          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Mailing Zip Code:</label>
            <input
              className={`form-control ${
                validationErrors[`eiMailingZipCode-${index}`]
                  ? "is-invalid"
                  : ""
              }`}
              type="text"
              value={educationalInstitution.eiMailingZipCode || ""}
              onChange={(e) => onInputChange(e, "eiMailingZipCode", index)}
            />
            {validationErrors[`eiMailingZipCode-${index}`] && (
              <div className="invalid-feedback">
                {validationErrors[`eiMailingZipCode-${index}`]}
              </div>
            )}
          </div>
        </>
      )}

      <div style={{ flex: "0 0 100%" }}>
        <button className="oxbtn btn-red" onClick={onDelete}>
          Delete
        </button>
      </div>
    </div>
  );
};

export default EducationalInstitutionForm;
